import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { cn } from '../../../utils/cn';

const CarePlanCoordination = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [carePlans, setCarePlans] = useState([
    {
      id: 1,
      patient: 'Sarah Johnson',
      mrn: 'MRN-2024-789456',
      planType: 'Comprehensive Care Plan',
      primaryDiagnosis: 'Type 2 Diabetes with Hypertension',
      status: 'active',
      lastUpdated: '2026-01-14 08:00',
      coordinators: ['Dr. Michael Chen', 'Nurse Emily Roberts'],
      approvals: [
        { provider: 'Dr. Michael Chen', role: 'Attending Physician', status: 'approved', date: '2026-01-14' },
        { provider: 'Dr. Sarah Williams', role: 'Cardiologist', status: 'approved', date: '2026-01-14' },
        { provider: 'Nurse Emily Roberts', role: 'Primary Nurse', status: 'pending', date: null }
      ],
      protocols: [
        {
          name: 'Diabetes Management Protocol',
          status: 'active',
          tasks: ['Blood glucose monitoring QID', 'Insulin administration per sliding scale', 'Dietary consultation']
        },
        {
          name: 'Hypertension Management',
          status: 'active',
          tasks: ['BP monitoring BID', 'Medication compliance', 'Sodium restriction']
        }
      ],
      medications: [
        { name: 'Metformin 1000mg', frequency: 'BID', status: 'active', reconciled: true },
        { name: 'Lisinopril 10mg', frequency: 'Daily', status: 'active', reconciled: true },
        { name: 'Atorvastatin 20mg', frequency: 'Daily', status: 'active', reconciled: false }
      ],
      dischargePlanning: {
        targetDate: '2026-01-16',
        status: 'in-progress',
        criteria: [
          { item: 'Stable vital signs for 24 hours', completed: true },
          { item: 'Patient education completed', completed: true },
          { item: 'Home health services arranged', completed: false },
          { item: 'Follow-up appointments scheduled', completed: false },
          { item: 'Medication reconciliation finalized', completed: false }
        ]
      }
    },
    {
      id: 2,
      patient: 'Robert Anderson',
      mrn: 'MRN-2024-456123',
      planType: 'Renal Care Plan',
      primaryDiagnosis: 'Chronic Kidney Disease Stage 4',
      status: 'active',
      lastUpdated: '2026-01-14 06:00',
      coordinators: ['Dr. Lisa Thompson', 'Dr. James Wilson'],
      approvals: [
        { provider: 'Dr. Lisa Thompson', role: 'Attending Physician', status: 'approved', date: '2026-01-14' },
        { provider: 'Dr. James Wilson', role: 'Nephrologist', status: 'approved', date: '2026-01-14' }
      ],
      protocols: [
        {
          name: 'Dialysis Protocol',
          status: 'active',
          tasks: ['Hemodialysis 3x per week', 'Fluid restriction monitoring', 'Vascular access care']
        }
      ],
      medications: [
        { name: 'Epoetin alfa', frequency: '3x weekly', status: 'active', reconciled: true },
        { name: 'Sevelamer 800mg', frequency: 'TID with meals', status: 'active', reconciled: true }
      ],
      dischargePlanning: {
        targetDate: '2026-01-15',
        status: 'ready',
        criteria: [
          { item: 'Dialysis schedule confirmed', completed: true },
          { item: 'Patient education completed', completed: true },
          { item: 'Transportation arranged', completed: true },
          { item: 'Follow-up with nephrologist scheduled', completed: true }
        ]
      }
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case'approved': case'ready':
        return 'bg-success/10 text-success border-success';
      case 'pending': case'in-progress':
        return 'bg-warning/10 text-warning border-warning';
      case 'inactive': case'rejected':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-6">
      {carePlans?.map((plan) => (
        <div key={plan?.id} className="bg-card rounded-lg border border-border shadow-elevation-1">
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-lg font-semibold text-foreground">{plan?.planType}</h2>
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                    getStatusColor(plan?.status)
                  )}>
                    {plan?.status?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    {plan?.patient} • {plan?.mrn}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Stethoscope" size={14} />
                    {plan?.primaryDiagnosis}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    Updated {plan?.lastUpdated}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Edit">
                  Edit Plan
                </Button>
                <Button variant="default" size="sm" iconName="Share2">
                  Share
                </Button>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-6">
            {/* Treatment Protocols */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="ClipboardList" size={16} className="text-primary" />
                Treatment Protocols
              </h3>
              <div className="space-y-3">
                {plan?.protocols?.map((protocol, idx) => (
                  <div key={idx} className="bg-background rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">{protocol?.name}</h4>
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                        getStatusColor(protocol?.status)
                      )}>
                        {protocol?.status}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {protocol?.tasks?.map((task, taskIdx) => (
                        <li key={taskIdx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="Check" size={14} className="text-success" />
                          {task}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Medication Reconciliation */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Pill" size={16} className="text-primary" />
                Medication Reconciliation
              </h3>
              <div className="bg-background rounded-lg border border-border">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Medication</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Frequency</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Reconciled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {plan?.medications?.map((med, idx) => (
                      <tr key={idx}>
                        <td className="p-3 text-sm font-medium text-foreground">{med?.name}</td>
                        <td className="p-3 text-sm text-muted-foreground">{med?.frequency}</td>
                        <td className="p-3">
                          <span className={cn(
                            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                            getStatusColor(med?.status)
                          )}>
                            {med?.status}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {med?.reconciled ? (
                            <Icon name="CheckCircle" size={18} className="text-success mx-auto" />
                          ) : (
                            <Icon name="AlertCircle" size={18} className="text-warning mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Multi-Disciplinary Approvals */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="UserCheck" size={16} className="text-primary" />
                Multi-Disciplinary Approvals
              </h3>
              <div className="space-y-2">
                {plan?.approvals?.map((approval, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {approval?.provider?.split(' ')?.map(n => n?.[0])?.join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{approval?.provider}</p>
                        <p className="text-sm text-muted-foreground">{approval?.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {approval?.date && (
                        <span className="text-sm text-muted-foreground">{approval?.date}</span>
                      )}
                      <span className={cn(
                        "inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border",
                        getStatusColor(approval?.status)
                      )}>
                        {approval?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Discharge Planning */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Home" size={16} className="text-primary" />
                Discharge Planning
              </h3>
              <div className="bg-background rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Target Discharge Date</p>
                    <p className="font-semibold text-foreground">{plan?.dischargePlanning?.targetDate}</p>
                  </div>
                  <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border",
                    getStatusColor(plan?.dischargePlanning?.status)
                  )}>
                    {plan?.dischargePlanning?.status?.replace('-', ' ')?.toUpperCase()}
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Discharge Criteria</p>
                  {plan?.dischargePlanning?.criteria?.map((criterion, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50">
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center",
                        criterion?.completed ? 'bg-success' : 'bg-muted'
                      )}>
                        {criterion?.completed && <Icon name="Check" size={14} className="text-white" />}
                      </div>
                      <span className={cn(
                        "text-sm",
                        criterion?.completed ? 'text-foreground' : 'text-muted-foreground'
                      )}>
                        {criterion?.item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CarePlanCoordination;